import { Form, TextField } from '@components/form-components';
import useFormState from '@hooks/useFormState';
import useTrackFormChanges from '@hooks/useTrackFormChanges';
import { Alert } from '@components/Alert';
import { Button, Link } from '@mui/material';
import { useForgotPassword } from '@reactQuery/auth';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { FormData, FormErrors } from './interfaces';
import { WindowPageTitle } from '@components/WindowPageTitle';
import { PanelTitle } from '../components/PanelTitle';
import { isNotEmpty, isValidEmailAddress } from '@helpers/formHelpers';
import { enqueueSnackbar } from 'notistack';

export const ForgotPassword: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>({
    email: location.state?.username || '',
  });
  const { formErrors, setFormErrors } = useTrackFormChanges(formData);

  const { mutate: forgotPassword, isPending: isRequestingResetPassword } =
    useForgotPassword();

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!isNotEmpty(formData.email || '')) {
      errors.email = t('errors.required');
    } else if (!isValidEmailAddress(formData.email || '')) {
      errors.email = t('errors.invalidEmail');
    }

    setFormErrors(errors);
  };

  const handleSubmit = () => {
    if (!Object.keys(formErrors).length) {
      forgotPassword(
        { email: formData.email as string },
        {
          onError: (error) => {
            enqueueSnackbar(t('forms:forgotPassword.messages.error'), {
              variant: 'error',
            });
          },
          onSuccess: () => {
            enqueueSnackbar(
              t('forms:forgotPassword.messages.success', {
                email: formData.email,
              }),
              {
                variant: 'success',
              }
            );

            navigate('/login');
          },
        }
      );
    }
  };

  return (
    <Fragment>
      <WindowPageTitle title={t('forms:forgotPassword.title')} />
      <PanelTitle title={t('forms:forgotPassword.title')} />
      <Alert severity="info" content={t('forms:forgotPassword.info')} />

      <Form onSubmit={handleSubmit} id="forgot-password-form">
        <TextField
          label={t('words.email')}
          placeholder={t('forms:login.placeholders.email')}
          error={formErrors.email}
          value={formData.email}
          onChange={(value) => {
            setFormFieldData('email', value);
          }}
        />
      </Form>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        onClick={validateForm}
        disabled={isRequestingResetPassword}
        form="forgot-password-form"
      >
        {t('forms:forgotPassword.buttons.send')}
      </Button>

      <Link
        component="button"
        type="button"
        onClick={() => {
          navigate('/login', {
            state: {
              username: formData.email,
            },
          });
        }}
        variant="body2"
        sx={{ alignSelf: 'center' }}
      >
        {t('forms:forgotPassword.buttons.goToLogin')}
      </Link>
    </Fragment>
  );
};
